// MyQueuePanel — "what needs my attention" widget for Mission Control.
//
// v1 surfaces:
//   * Proposal sections awaiting my signoff (contributor_review or
//     final_review depending on my capacity on that opportunity).
//
// The API is intentionally list-like so future sources (Nuggets
// assigned to me, unread @mentions, Deal Room diligence items,
// etc.) can drop in without a redesign.  Add them by pushing new
// entries into `items` — each row needs { id, kind, title,
// subtitle, opened_at, actions[] }.

import React from 'react';
import { CheckCircle2, XCircle, ArrowRight, Clock, ThumbsUp, ThumbsDown, RotateCcw } from 'lucide-react';
import {
  loadMyPendingApprovals,
  submitProposalSectionDecision,
} from '../../db.js';

const KIND_META = {
  approval_contributor: { label: 'Contributor review', color: '#60A5FA' },
  approval_final:       { label: 'Final approval',     color: '#E87722' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)    return `${secs}s`;
  if (secs < 3600)  return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
}

export default function MyQueuePanel({ currentUser, setView, onOpenOpportunity, showToast }) {
  const [items,    setItems]    = React.useState(null);
  const [busyId,   setBusyId]   = React.useState(null);

  const refresh = React.useCallback(async () => {
    if (!currentUser?.id || !currentUser?.orgId) return;
    const approvals = await loadMyPendingApprovals(currentUser.id, currentUser.orgId);
    setItems(approvals.map(a => ({
      id:         `approval:${a.section_id}`,
      kind:       a.review_kind === 'final' ? 'approval_final' : 'approval_contributor',
      title:      a.section_title || '(untitled section)',
      subtitle:   a.opportunity_name,
      opened_at:  a.updated_at,
      _raw:       a,
    })));
  }, [currentUser?.id, currentUser?.orgId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const decide = async (item, decision) => {
    setBusyId(item.id);
    const capacity = item._raw.review_kind === 'final' ? 'final_approver' : 'contributor';
    const result = await submitProposalSectionDecision({
      sectionId: item._raw.section_id,
      userId:    currentUser.id,
      capacity,
      decision,
    });
    setBusyId(null);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    const verb = decision === 'approved' ? 'Approved' : decision === 'rejected' ? 'Rejected' : 'Sent back for changes';
    showToast?.(`${verb} — ${item.title}`);
    refresh();
  };

  const openOpp = (item) => {
    onOpenOpportunity?.(item._raw.opportunity_id);
    setView?.('proposal_coach');
  };

  const orange = '#E87722';
  const text   = '#F0F4F8';
  const muted  = 'rgba(255,255,255,0.55)';
  const border = 'rgba(255,255,255,0.10)';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${border}`,
      borderRadius: 10,
      padding: 16,
      fontFamily: "'Montserrat',sans-serif",
      color: text,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.14em', color: orange, fontWeight: 800, textTransform: 'uppercase' }}>
          My Queue
        </div>
        <div style={{ flex: 1 }} />
        {items && items.length > 0 && (
          <span style={{ fontSize: 11, color: muted }}>{items.length} pending</span>
        )}
        <button
          onClick={refresh}
          title="Refresh"
          aria-label="Refresh queue"
          style={{
            marginLeft: 8, background: 'transparent', border: 'none',
            color: muted, cursor: 'pointer', padding: 2, display: 'flex',
          }}
        >
          <RotateCcw size={12} />
        </button>
      </div>

      {items === null && (
        <div style={{ padding: 14, color: muted, fontSize: 12 }}>Loading…</div>
      )}

      {items && items.length === 0 && (
        <div style={{
          padding: 18, borderRadius: 6, background: 'rgba(255,255,255,0.02)',
          border: `1px dashed ${border}`, color: muted, fontSize: 12, textAlign: 'center',
        }}>
          <CheckCircle2 size={14} style={{ verticalAlign: 'middle', marginRight: 6, color: '#22C55E' }} />
          Inbox zero. Nothing waiting for you right now.
        </div>
      )}

      {items && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto' }}>
          {items.map(it => {
            const meta = KIND_META[it.kind] || { label: it.kind, color: muted };
            const isBusy = busyId === it.id;
            return (
              <div
                key={it.id}
                className="a11y-density-card"
                style={{
                  padding: '10px 12px', border: `1px solid ${border}`, borderRadius: 8,
                  background: 'rgba(255,255,255,0.02)',
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 4,
                    background: `${meta.color}18`, color: meta.color,
                    border: `1px solid ${meta.color}55`,
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>{meta.label}</span>
                  <span style={{ color: muted, fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Clock size={10} /> {timeAgo(it.opened_at)}
                  </span>
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={() => openOpp(it)}
                    title="Open in Proposal Coach"
                    style={{
                      background: 'transparent', border: `1px solid ${border}`,
                      color: text, padding: '3px 8px', borderRadius: 4,
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                      textTransform: 'uppercase', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                    Open <ArrowRight size={11} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.title}
                    </div>
                    <div style={{ fontSize: 11, color: muted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.subtitle}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      disabled={isBusy}
                      onClick={() => decide(it, 'approved')}
                      title="Approve"
                      style={{
                        background: '#22C55E', color: '#0D1B2A',
                        border: 'none', padding: '5px 10px', borderRadius: 4,
                        fontSize: 10, fontWeight: 800, letterSpacing: '0.04em',
                        textTransform: 'uppercase', cursor: isBusy ? 'default' : 'pointer',
                        opacity: isBusy ? 0.5 : 1,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                      <ThumbsUp size={11} /> Approve
                    </button>
                    <button
                      disabled={isBusy}
                      onClick={() => decide(it, 'changes_requested')}
                      title="Send back for changes"
                      style={{
                        background: 'transparent', color: '#F59E0B',
                        border: `1px solid #F59E0B55`,
                        padding: '5px 10px', borderRadius: 4,
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                        textTransform: 'uppercase', cursor: isBusy ? 'default' : 'pointer',
                        opacity: isBusy ? 0.5 : 1,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                      Changes
                    </button>
                    <button
                      disabled={isBusy}
                      onClick={() => decide(it, 'rejected')}
                      title="Reject"
                      style={{
                        background: 'transparent', color: '#EF4444',
                        border: `1px solid #EF444455`,
                        padding: '5px 10px', borderRadius: 4,
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                        textTransform: 'uppercase', cursor: isBusy ? 'default' : 'pointer',
                        opacity: isBusy ? 0.5 : 1,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                      <ThumbsDown size={11} /> Reject
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
